import * as Yup from 'yup';
import { useState, useCallback } from 'react';
import { useFormik, FormikContext, Field, Form, FormikHelpers } from 'formik';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';

import { TextField } from 'formik-material-ui';
import Container from '@material-ui/core/Container';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import AssignmentInd from '@material-ui/icons/AssignmentInd';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import Box from '@material-ui/core/Box';

import Head from '../../../components/Head';
import Card from '../../../components/Auth/Card';
import AuthHeader from '../../../components/Auth/Header';
import AuthFooter from '../../../components/Auth/Footer';
import BackToLogin from '../../../components/Auth/BackToLogin';
import ErrorMessage from '../../../components/ErrorMessage';
import useAuthStyles from '../../../styles/pages/auth';
import useStyles from './styles';
import { useAuth } from '../../../contexts/auth';

const validationSchema = Yup.object({
  gpid: Yup.string()
    .matches(/^[0-9]*$/, 'Apenas números são permitidos')
    .min(8, 'Deve ter pelo menos ${min} caracteres') // eslint-disable-line no-template-curly-in-string
    .required('GPID é obrigatório'),
}).required();

type Values = Yup.InferType<typeof validationSchema>;

function FormH1N1() {
  const auth = useAuth();
  const authClasses = useAuthStyles();
  const classes = useStyles();
  const history = useHistory();

  const [hasError, setHasError] = useState(false);
  const [hasSuccess, setHasSuccess] = useState(false);

  const handleClose = () => {
    setHasError(false);
    setHasSuccess(false);
  };

  const handleFormik = useCallback(
    async (values: Values, formikHelpers: FormikHelpers<Values>) => {
      try {
        await auth.loginWithGPID(values.gpid);
        history.push('/minha-saude/vacina');
        formikHelpers.resetForm({ values: {} as Values });
        setHasSuccess(true);
      } catch (error) {
        setHasError(true);
      }
      formikHelpers.setSubmitting(false);
    },
    [auth, history]
  );

  const formik = useFormik({
    validationSchema: validationSchema,
    initialValues: { gpid: '' },
    onSubmit: handleFormik,
  });

  const { isSubmitting } = formik;

  return (
    <FormikContext.Provider value={formik}>
      <Container maxWidth="sm" style={{ zIndex: 2 }}>
        <Head>Gripe</Head>
        <Form className={clsx(authClasses.container, classes.container)}>
          <Grid
            container
            className={clsx(authClasses.cardWrapper, classes.wrapper)}
          >
            <Box flex={1}></Box>
            <AuthHeader outside />
            <Card className={classes.card}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    className={authClasses.descriptionContainer}
                  >
                    <Typography variant="h1">Vacinação Contra Gripe</Typography>
                    <Typography>
                      Utilize a sua matrícula da PepsiCo para saber mais sobre a
                      campanha de vacinação
                    </Typography>
                  </Grid>
                  <Grid item xs={12} className={authClasses.inputsContainer}>
                    <Field
                      component={TextField}
                      id="gpid"
                      name="gpid"
                      label="GPID"
                      size="small"
                      inputProps={{
                        maxLength: 8,
                      }}
                      InputProps={{
                        className: authClasses.inputs,
                        endAdornment: (
                          <InputAdornment position="end">
                            <AssignmentInd />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ErrorMessage
                      open={hasError || hasSuccess}
                      handleClose={handleClose}
                      title={hasError ? 'Erro' : 'Sucesso'}
                      severity={hasError ? 'error' : 'success'}
                      message={
                        hasError && (
                          <>
                            <strong>GPID</strong> não localizado
                          </>
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      size="large"
                      disabled={isSubmitting}
                      className={authClasses.submitButton}
                    >
                      {isSubmitting ? 'Aguarde...' : 'Entrar'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <BackToLogin />
            <Box
              flex={1}
              paddingBottom={2}
              width="100%"
              display="flex"
              alignItems="flex-end"
            >
              <AuthFooter />
            </Box>
          </Grid>
        </Form>
      </Container>
    </FormikContext.Provider>
  );
}

export default FormH1N1;
